import Overlay from "ol/Overlay";
import Map from "ol/Map";
import { IOrnament, IOrnamentQueryResponse } from "./interfaces";
import { Coordinate } from "ol/coordinate";
import { debounce } from "debounce";
import { ORNAMENT_BASELINE_SIZE, ORNAMENT_POLLING_INTERVAL, ORNAMENT_TAB_NO, SVIECKY } from "./constants";

declare var CintorinKarta: {
    updateGraveCard:(hId: number, cId: number, selectedTabIndex?: number, zId?: number) => Promise<void>
};

declare var CintorinMapa: {
    selectGrave:(locationId: string, cintorinId:number) => void
};

interface OverlayPair {
    id: number,
    overlay: Overlay
}

var overlays: OverlayPair[] = [];
var assignedCoords: Coordinate[];
var cintorinId: number | undefined;
var map: Map | undefined;
var baseUrl = "";
var ornamentsVisible = true;

export function clearCurrentOrnaments() {
    if (map) {
        overlays.forEach(o => {
            map.removeOverlay(o.overlay);
        });
    }
    overlays = [];
    assignedCoords = [];
}

export async function loadOrnaments(cid:number, parentMap:Map, siteBaseUrl: string): Promise<void> {
    cintorinId = cid;
    map = parentMap;
    baseUrl = siteBaseUrl;
    await updateOrnaments(cid, true);
}

export function scaleOrnaments(resolution: number) {
    let percentage = 100;
    if (resolution > 0.2) { 
        percentage = 20;
    } else if (resolution < 0.01) { 
        percentage = 100;
    } else {
        percentage = 20 + (1-(resolution-0.01)/0.19)*80;
    }
    let size = ORNAMENT_BASELINE_SIZE * percentage / 100;
    let ornaments = document.querySelectorAll("div.ozdoba-map-scale");
    ornaments.forEach(e => {
        (e as HTMLDivElement).style.width = size+"px";
        (e as HTMLDivElement).style.height = size+"px";
    });
}

export async function triggerOrnamentUpdate() {
    await updateOrnaments(cintorinId, false);
}

export function setOrnamentVisibility(visibility: boolean) {
    ornamentsVisible = visibility;
}

async function updateOrnaments(cid:number, shouldPoll:boolean) {
    if (cid === cintorinId) {
        await fetch(baseUrl+"/api/getOrnaments.php", {
            method: "POST",
            headers: new Headers({
                'Content-Type': 'application/x-www-form-urlencoded'
            }),
            body: "cId="+encodeURIComponent(cintorinId)
        }).then(response => {
            if (response && response.ok) {
                return response.json();
            }
        }).then(json => {
            if (json) {
                overlays.map(o => map.removeOverlay(o.overlay));
                overlays = [];
                assignedCoords = [];
                (json as IOrnamentQueryResponse).ornaments.map(ozdoba => {
                    processOrnament(ozdoba)
                });
                scaleOrnaments(map.getView().getResolution());
                if (shouldPoll) {
                    setTimeout(updateOrnaments, ORNAMENT_POLLING_INTERVAL, cid, shouldPoll);
                }
            }
        });
    }
}

function processOrnament(ozdoba :IOrnament) {
    // zobrazujem prvy ornament pre kazde hrobove miesto
    if (typeof (ozdoba.coordinates) !== "undefined" 
        && ozdoba.type !== "N/A"
        && assignedCoords.every(c => c[0] !== ozdoba.coordinates[0] && c[1] !== ozdoba.coordinates[1])
    ) {
        let container = document.createElement("div");
        container.classList.add("ozdoba", "ozdoba-map-scale", "ozdoba-"+ozdoba.type);
        if (!ornamentsVisible) {
            container.classList.add("ozdoba-hide");
        }
        container.setAttribute("data-grave-id", ""+ozdoba.graveId);
        container.setAttribute("data-grave-identifier", ""+ozdoba.identifier);
        container.addEventListener("click", debounce(handleOrnamentClicked, 100));
        let item = new Overlay({
            element: container,
            position: ozdoba.coordinates,
            positioning: "center-center",
            className: "ozdoba-container",
            stopEvent: false
        });
        updateOrnamentState(item, ozdoba, container);
        overlays.push({ id: ozdoba.graveId, overlay: item });
        assignedCoords.push(ozdoba.coordinates);
        if (map)
            map.addOverlay(item);
    }
}

function updateOrnamentState(overlay:Overlay, ozdoba:IOrnament, element:HTMLDivElement) {
    let currentTime = Date.now()/1000; // prevod na sekundy
    if (currentTime < ozdoba.endTime) {
        let nextUpdateTime = ozdoba.endTime;
        if (SVIECKY.includes(ozdoba.type)) { // ak je ornament sviecka ktora hori
            element.classList.remove("ozdoba-100-percent", "ozdoba-80-percent", "ozdoba-60-percent", "ozdoba-40-percent", "ozdoba-20-percent");
            let currentPercentage = (currentTime - ozdoba.startTime)/(ozdoba.endTime - ozdoba.startTime);
            if (currentPercentage < 0.20) {
                element.classList.add("ozdoba-100-percent");
                nextUpdateTime = 0.2*ozdoba.endTime + 0.8*ozdoba.startTime;
            } else if (currentPercentage >= 0.20 && currentPercentage < 0.40) {
                element.classList.add("ozdoba-80-percent");
                nextUpdateTime = 0.4*ozdoba.endTime + 0.6*ozdoba.startTime;
            } else if (currentPercentage >= 0.40 && currentPercentage < 0.60) {
                element.classList.add("ozdoba-60-percent");
                nextUpdateTime = 0.6*ozdoba.endTime + 0.4*ozdoba.startTime;
            } else if (currentPercentage >= 0.60 && currentPercentage < 0.80) {
                element.classList.add("ozdoba-40-percent");
                nextUpdateTime = 0.8*ozdoba.endTime + 0.2*ozdoba.startTime;
            } else if (currentPercentage >= 0.8) {
                element.classList.add("ozdoba-20-percent");
                nextUpdateTime = ozdoba.endTime;
            }
        }
        setTimeout(updateOrnamentState, (nextUpdateTime - currentTime)*1000, overlay, ozdoba, element);
    } else {
        let map = overlay.getMap();
        if (map) {
            map.removeOverlay(overlay);
        }
        overlays = overlays.filter(o => o.id != ozdoba.id);
        assignedCoords = assignedCoords.filter(c => c[0] !== ozdoba.coordinates[0] && c[1] !== ozdoba.coordinates[1]);
    }
}

function handleOrnamentClicked(e: MouseEvent) {
    e.stopPropagation();
    CintorinKarta.updateGraveCard(this.getAttribute("data-grave-id"), cintorinId, ORNAMENT_TAB_NO).then(() => {
        if (document.getElementById("cardIdentifier")) {
            CintorinMapa.selectGrave((document.getElementById("cardIdentifier") as HTMLInputElement).value, cintorinId);
        }
    });
}